<template>
  <div v-if="showModule" class="procedures-module">
    <div class="procedures-shell">
      <Tabs
        :value="activeTab"
        @update:value="activeTab = Number($event)"
        class="custom-tabs procedures-tabs"
      >
        <TabList>
          <Tab :value="0">{{ t("wkt.client.procedures.tabs.pending").toUpperCase() }}</Tab>
          <Tab :value="1">{{ t("wkt.client.procedures.tabs.historical").toUpperCase() }}</Tab>
        </TabList>

        <TabPanels>
          <TabPanel :value="0">
            <div v-if="loading" class="procedures-state">
              <div v-if="isDesktop" class="procedures-skeleton">
                <div class="procedures-skeleton__panel">
                  <Skeleton width="11rem" height="1rem" borderRadius="999px" />
                  <Skeleton width="100%" height="12rem" borderRadius="0" />
                </div>
                <div class="procedures-skeleton__panel">
                  <Skeleton width="8rem" height="1rem" borderRadius="999px" />
                  <Skeleton width="100%" height="12rem" borderRadius="0" />
                </div>
              </div>
              <div v-else class="procedures-skeleton procedures-skeleton--mobile">
                <article v-for="index in 2" :key="`pending-${index}`" class="procedures-skeleton-card">
                  <div class="procedures-skeleton-card__header">
                    <Skeleton shape="circle" size="2rem" />
                    <div class="procedures-skeleton-card__header-copy">
                      <Skeleton width="62%" height="0.95rem" />
                      <Skeleton width="42%" height="0.8rem" />
                    </div>
                    <Skeleton width="4.5rem" height="1.4rem" borderRadius="999px" />
                  </div>
                  <div class="procedures-skeleton-card__employee">
                    <Skeleton shape="circle" size="2.35rem" />
                    <div class="procedures-skeleton-card__employee-copy">
                      <Skeleton width="8.5rem" height="0.85rem" />
                      <Skeleton width="7rem" height="0.75rem" />
                    </div>
                  </div>
                  <div class="procedures-skeleton-card__details">
                    <Skeleton width="100%" height="2.5rem" />
                    <Skeleton width="100%" height="2.5rem" />
                    <Skeleton width="100%" height="2.5rem" />
                    <Skeleton width="100%" height="2.5rem" />
                  </div>
                  <div class="procedures-skeleton-card__actions">
                    <Skeleton width="6.25rem" height="2rem" borderRadius="999px" />
                    <Skeleton width="6.25rem" height="2rem" borderRadius="999px" />
                  </div>
                </article>
              </div>
            </div>

            <div v-else-if="error" class="procedures-state">
              <div class="procedures-error">
                <EmptyState
                  :title="t('wkt.client.procedures.error-state.title')"
                  :description="t('wkt.client.procedures.error-state.description')"
                />
                <Button
                  class="footer-primary-button"
                  text
                  :label="t('wkt.client.procedures.actions.retry')"
                  @click="loadProcedures"
                />
              </div>
            </div>

            <div v-else class="procedures-content">
              <section v-if="approverPendingRequests.length" class="procedures-section">
                <header class="procedures-section__header">
                  <div class="procedures-section__title">
                    <i class="pi pi-users" aria-hidden="true"></i>
                    <h2>{{ t("wkt.client.procedures.approve_pending_request") }}</h2>
                    <span class="procedures-section__count">{{ approverPendingRequests.length }}</span>
                  </div>
                </header>

                <ProceduresTable
                  v-if="isDesktop"
                  :rows="approverPendingRequests"
                  :definitions="definitionsById"
                  mode="approver"
                  @approve="approveRequest"
                  @deny="denyRequest"
                  @employee="showEmployeeDetails"
                  @approver-calendar="openApproverCalendar"
                  @expense="showExpenseSheet"
                />

                <div v-else class="procedures-card-grid">
                  <ProcedureRequestCard
                    v-for="request in approverPendingRequests"
                    :key="request.id"
                    :request="request"
                    :definition="definitionForRequest(request)"
                    mode="approver"
                    @approve="approveRequest"
                    @deny="denyRequest"
                    @employee="showEmployeeDetails"
                    @approver-calendar="openApproverCalendar"
                    @expense="showExpenseSheet"
                  />
                </div>
              </section>

              <section class="procedures-section">
                <header class="procedures-section__header">
                  <div class="procedures-section__title">
                    <i class="pi pi-send" aria-hidden="true"></i>
                    <h2>{{ t("wkt.client.procedures.my_requests") }}</h2>
                    <span class="procedures-section__count">{{ myPendingRequests.length }}</span>
                  </div>
                </header>

                <ProceduresTable
                  v-if="myPendingRequests.length && isDesktop"
                  :rows="myPendingRequests"
                  :definitions="definitionsById"
                  mode="own-pending"
                  @edit="editRequest"
                  @employee="showEmployeeDetails"
                  @own-calendar="openOwnCalendar"
                  @expense="showExpenseSheet"
                />

                <div v-else-if="myPendingRequests.length" class="procedures-card-grid">
                  <ProcedureRequestCard
                    v-for="request in myPendingRequests"
                    :key="request.id"
                    :request="request"
                    :definition="definitionForRequest(request)"
                    mode="own-pending"
                    @edit="editRequest"
                    @employee="showEmployeeDetails"
                    @own-calendar="openOwnCalendar"
                    @expense="showExpenseSheet"
                  />
                </div>

                <div v-else class="procedures-empty procedures-empty--section">
                  <EmptyState
                    :title="t('wkt.client.procedures.request-empty-state.title')"
                    :description="t('wkt.client.procedures.request-empty-state.description')"
                  />
                </div>
              </section>
            </div>
          </TabPanel>

          <TabPanel :value="1">
            <div v-if="loading" class="procedures-state">
              <div v-if="isDesktop" class="procedures-skeleton">
                <div class="procedures-skeleton__panel">
                  <Skeleton width="14rem" height="1rem" borderRadius="999px" />
                  <Skeleton width="100%" height="14rem" borderRadius="0" />
                </div>
              </div>
              <div v-else class="procedures-skeleton procedures-skeleton--mobile">
                <article class="procedures-skeleton-card">
                  <div class="procedures-skeleton-card__header">
                    <Skeleton shape="circle" size="2rem" />
                    <div class="procedures-skeleton-card__header-copy">
                      <Skeleton width="58%" height="0.95rem" />
                      <Skeleton width="38%" height="0.8rem" />
                    </div>
                    <Skeleton width="4.5rem" height="1.4rem" borderRadius="999px" />
                  </div>
                  <div class="procedures-skeleton-card__details">
                    <Skeleton width="100%" height="2.5rem" />
                    <Skeleton width="100%" height="2.5rem" />
                    <Skeleton width="100%" height="2.5rem" />
                    <Skeleton width="100%" height="2.5rem" />
                  </div>
                  <div class="procedures-skeleton-card__actions">
                    <Skeleton width="7rem" height="2rem" borderRadius="999px" />
                  </div>
                </article>
              </div>
            </div>

            <div v-else-if="error" class="procedures-state">
              <div class="procedures-error">
                <EmptyState
                  :title="t('wkt.client.procedures.error-state.title')"
                  :description="t('wkt.client.procedures.error-state.description')"
                />
                <Button
                  class="footer-primary-button"
                  text
                  :label="t('wkt.client.procedures.actions.retry')"
                  @click="loadProcedures"
                />
              </div>
            </div>

            <div v-else class="procedures-content">
              <section class="procedures-section">
                <header class="procedures-section__header">
                  <div class="procedures-section__title">
                    <i class="pi pi-history" aria-hidden="true"></i>
                    <h2>{{ t("wkt.client.procedures.my_requests") }}</h2>
                    <span class="procedures-section__count">{{ historicalRequests.length }}</span>
                  </div>
                  <div class="procedures-section__controls">
                    <Select
                      v-model="selectedFilterId"
                      :options="filterOptions"
                      optionLabel="label"
                      optionValue="value"
                      :placeholder="t('wkt.client.procedures.filter_requests')"
                      class="procedures-filter"
                    />
                    <Button
                      v-if="selectedFilterId != null"
                      class="footer-button-cancel"
                      text
                      icon="pi pi-times"
                      :label="t('wkt.client.common.clear')"
                      @click="selectedFilterId = null"
                    />
                  </div>
                </header>

                <ProceduresTable
                  v-if="historicalRequests.length && isDesktop"
                  :rows="historicalRequests"
                  :definitions="definitionsById"
                  mode="history"
                  @show="showHistoricalRequest"
                  @employee="showEmployeeDetails"
                />

                <div v-else-if="historicalRequests.length" class="procedures-card-grid">
                  <ProcedureRequestCard
                    v-for="request in historicalRequests"
                    :key="request.id"
                    :request="request"
                    :definition="definitionForRequest(request)"
                    mode="history"
                    @show="showHistoricalRequest"
                    @employee="showEmployeeDetails"
                  />
                </div>

                <div v-else class="procedures-empty">
                  <EmptyState
                    :title="t('wkt.client.procedures.record-empty-state.title')"
                    :description="t('wkt.client.procedures.record-empty-state.description')"
                  />
                </div>
              </section>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>

    <Button
      v-if="showCreateFab"
      icon="pi pi-plus"
      size="large"
      class="custom-add-button"
      :class="{ 'custom-add-button-mobile': !isDesktop }"
      rounded
      severity="var(--primary-color)"
      @click="openCreateRequest"
      v-tooltip="{ value: t('wkt.client.procedures.new_request') }"
    />

    <ProcedureSelectorDialog
      v-model:visible="showSelectorDialog"
      :title="t('wkt.client.procedures.new_request')"
      :definitions="enabledDefinitions"
      @select="selectDefinition"
    />

    <ProcedureRequestDialog
      :visible="showRequestDialog"
      :mode="requestDialogMode"
      :request="selectedDialogRequest"
      :definition="selectedDialogDefinition"
      :session-information="sessionInformation"
      :draft-seed="requestDialogDraftSeed"
      :approver-employee="selectedApproverEmployee"
      :attachment-loading="requestDialogAttachmentLoading"
      :action-loading="requestDialogActionLoading"
      :project-options="dialogProjectOptions"
      :project-options-loading="dialogProjectOptionsLoading"
      :absence-category-options="dialogAbsenceCategoryOptions"
      :absence-category-options-loading="dialogAbsenceCategoryOptionsLoading"
      :loading="requestDialogLoading"
      :error="requestDialogError"
      :notFound="requestDialogNotFound"
      @update:visible="handleRequestDialogVisibility"
      @retry="retryShowHistoricalRequest"
      @expense="showExpenseSheet"
      @calendar="openOwnCalendar"
      @draft-change="handleRequestDialogDraftChange"
      @upload-document="uploadProcedureDocument"
      @remove-document="removeProcedureDocument"
      @submit="submitProcedureDialog"
      @delete="deleteProcedureDialog"
      @decline="declineProcedureDialog"
    />

    <ConfirmDialog
      ref="requestActionConfirmDialog"
      :title="requestActionConfirmDialogTitle"
      :message="requestActionConfirmDialogMessage"
      :titleOk="requestActionConfirmDialogOkLabel"
      :titleCancel="t('wkt.client.common.dialog-buttons.cancel')"
      @confirmed="confirmProcedureDialogAction"
      @canceled="pendingProcedureDialogConfirmAction = null"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import type { AxiosError } from "axios";
import { gwtApi } from "@wkt/api/gwt/gwtAPI";
import type { Project, SessionInformation } from "@wkt/api/gwt/gwtDTO";
import { documentsApi } from "@wkt/api/documents/documentsAPI";
import { proceduresApi } from "@wkt/api/procedures";
import {
  ProcedureRequestStatus,
  type FullProcedureDefinitionDTO,
  type FullEmployeeDTO,
  type FullProcedureRequestDTO,
  type GenericServiceResponseDTO,
  type PaidAbsenceCategoryDTO,
  type ProcedureDataDTO,
  type ProcedureDocumentDTO,
  type ProcedureDocumentUploadResponseDTO,
  type ProcedureServiceResponseDTO,
} from "@wkt/api/procedures";
import EmptyState from "@wkt/components/common/EmptyState.vue";
import ConfirmDialog from "@wkt/components/common/ConfirmDialog.vue";
import { useAlert } from "@wkt/composables/useAlert";
import ProcedureRequestDialog from "./ProcedureRequestDialog.vue";
import ProcedureSelectorDialog from "./ProcedureSelectorDialog.vue";
import ProceduresTable from "./ProceduresTable.vue";
import ProcedureRequestCard from "./ProcedureRequestCard.vue";
import type {
  ProcedureRequestDialogDraftState,
  ProcedureRequestDialogMode,
  ProcedureRequestDialogSubmitPayload,
} from "./procedureRequestDialogContract";

const { t } = useI18n();
const { showAlert } = useAlert();

const showModule = ref(false);
const loading = ref(true);
const error = ref(false);
const activeTab = ref(0);
const selectedFilterId = ref<number | null>(null);
const showSelectorDialog = ref(false);
const showRequestDialog = ref(false);
const isDesktop = ref(window.innerWidth >= 992);
const requestDialogMode = ref<ProcedureRequestDialogMode>("read-only");
const requestDialogRequestId = ref<number | null>(null);
const requestDialogDefinitionId = ref<number | null>(null);
const requestDialogLoading = ref(false);
const requestDialogError = ref(false);
const requestDialogNotFound = ref(false);
const selectedRequestSummary = ref<FullProcedureRequestDTO | null>(null);
const selectedRequestDetail = ref<FullProcedureRequestDTO | null>(null);
const requestDialogActionLoading = ref(false);
const requestDialogAttachmentLoading = ref(false);
const requestDialogDraftSeed = ref<ProcedureRequestDialogDraftState | null>(null);
const requestDialogDraftSnapshot = ref<ProcedureRequestDialogDraftState | null>(null);
const requestDialogDocuments = ref<ProcedureDocumentDTO[]>([]);
const requestDialogDocumentIds = ref<number[]>([]);
const dialogProjectOptions = ref<Array<{ label: string; value: number }>>([]);
const dialogProjectOptionsLoading = ref(false);
const dialogAbsenceCategoryOptions = ref<Array<{ label: string; value: number | null }>>([]);
const dialogAbsenceCategoryOptionsLoading = ref(false);
const procedureData = ref<ProcedureDataDTO>({
  procedureRequests: [],
  approverRequests: [],
  approverEmployees: [],
});

const sessionInformation = ref<SessionInformation | null>(null);
const currentEmployee = computed(() => (sessionInformation.value as any)?.employee || null);

const definitions = computed<FullProcedureDefinitionDTO[]>(() => {
  const data = sessionInformation.value as any;
  return (data?.procedures || []) as FullProcedureDefinitionDTO[];
});

const enabledDefinitions = computed(() =>
  definitions.value
    .filter((definition) => definition?.enabled)
    .sort((left, right) => String(left?.name || "").localeCompare(String(right?.name || "")))
);

const definitionsById = computed<Record<number, FullProcedureDefinitionDTO>>(() => {
  const data = sessionInformation.value as any;
  const allDefinitions = ((data?.proceduresAll || data?.procedures || []) as FullProcedureDefinitionDTO[]);

  return allDefinitions.reduce<Record<number, FullProcedureDefinitionDTO>>((acc, definition) => {
    if (definition?.id != null) {
      acc[definition.id] = definition;
    }
    return acc;
  }, {});
});

const filterOptions = computed(() => [
  { label: t("wkt.client.common.all"), value: null },
  ...enabledDefinitions.value.map((definition) => ({
    label: definition.name || "---",
    value: definition.id,
  })),
]);

const approverPendingRequests = computed(() =>
  (procedureData.value.approverRequests || []).filter(
    (request) =>
      request.requestStatus != null &&
      request.requestStatus !== ProcedureRequestStatus.PREPARING &&
      request.requestStatus < ProcedureRequestStatus.CANCELED
  )
);

const myPendingRequests = computed(() =>
  (procedureData.value.procedureRequests || []).filter(
    (request) => request.requestStatus === ProcedureRequestStatus.PENDING
  )
);

const historicalRequests = computed(() =>
  (procedureData.value.procedureRequests || []).filter((request) => {
    if (
      request.requestStatus == null ||
      request.requestStatus <= ProcedureRequestStatus.PENDING
    ) {
      return false;
    }

    if (selectedFilterId.value == null) {
      return true;
    }

    return request.requestType === selectedFilterId.value;
  })
);

const showCreateFab = computed(
  () => !loading.value && activeTab.value === 0 && enabledDefinitions.value.length > 0
);

const selectedDialogRequest = computed<FullProcedureRequestDTO | null>(() => {
  if (!selectedRequestSummary.value && !selectedRequestDetail.value) {
    return null;
  }

  const documentIdsCsv =
    requestDialogDocumentIds.value.length > 0
      ? requestDialogDocumentIds.value.join(",")
      : null;

  return {
    ...(selectedRequestSummary.value || {}),
    ...(selectedRequestDetail.value || {}),
    documentIds: documentIdsCsv,
    documents: requestDialogDocuments.value,
  } as FullProcedureRequestDTO;
});

const selectedDialogDefinition = computed<FullProcedureDefinitionDTO | null>(() => {
  const requestType = selectedDialogRequest.value?.requestType ?? requestDialogDefinitionId.value;
  return requestType == null ? null : definitionsById.value[requestType] || null;
});

const selectedApproverEmployee = computed<FullEmployeeDTO | null>(() => {
  const employeeId = selectedDialogRequest.value?.employeeId;
  if (employeeId == null) {
    return null;
  }

  return (
    (procedureData.value.approverEmployees || []).find(
      (employee) => employee.id === employeeId
    ) || null
  );
});

interface ProcedureDialogPayload {
  mode?: ProcedureRequestDialogMode;
  requestId?: number | string | null;
  definitionId?: number | string | null;
}

interface ProceduresModulePayload {
  loading?: boolean;
  dialog?: ProcedureDialogPayload | null;
}

interface PersistedProcedureDialogState {
  version: 1;
  mode: ProcedureRequestDialogMode;
  requestId: number | null;
  definitionId: number | null;
  draft: ProcedureRequestDialogDraftState | null;
}

interface ConfirmDialogHandle {
  confirm: (payload?: unknown) => void;
}

type ProcedureDialogConfirmAction =
  | { type: "delete" }
  | { type: "decline"; payload: ProcedureRequestDialogSubmitPayload };

const PROCEDURE_DIALOG_STORAGE_KEY = "procedures_vue_dialog_state";
const requestActionConfirmDialog = ref<ConfirmDialogHandle | null>(null);
const pendingProcedureDialogConfirmAction =
  ref<ProcedureDialogConfirmAction | null>(null);

function formatProjectOptionLabel(project?: Project | null): string {
  const shortName = project?.shortName?.trim();
  const description = project?.description?.trim();

  if (shortName && description) {
    return `${shortName} - ${description}`;
  }

  return shortName || description || project?.name || String(project?.id || "---");
}

function definitionForRequest(request: FullProcedureRequestDTO): FullProcedureDefinitionDTO | null {
  const requestType = request.requestType;
  return requestType == null ? null : definitionsById.value[requestType] || null;
}

function findRequestById(requestId: number): FullProcedureRequestDTO | null {
  const requests = [
    ...(procedureData.value.procedureRequests || []),
    ...(procedureData.value.approverRequests || []),
  ];
  return requests.find((request) => request.id === requestId) || null;
}

function parseNumericIdentifier(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function parseProceduresModulePayload(payload?: unknown): ProceduresModulePayload | null {
  if (!payload) {
    return null;
  }

  if (typeof payload === "string") {
    const trimmed = payload.trim();
    if (!trimmed) {
      return null;
    }

    try {
      const parsed = JSON.parse(trimmed);
      return parsed && typeof parsed === "object"
        ? (parsed as ProceduresModulePayload)
        : null;
    } catch {
      return null;
    }
  }

  return typeof payload === "object" ? (payload as ProceduresModulePayload) : null;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function waitForSessionInformation(attempt = 0): Promise<SessionInformation | null> {
  const nextSessionInformation = gwtApi.getSessionInformation();
  if (nextSessionInformation?.employee && nextSessionInformation?.company) {
    return nextSessionInformation;
  }

  if (attempt >= 40) {
    return nextSessionInformation;
  }

  await wait(250);
  return waitForSessionInformation(attempt + 1);
}

async function ensureSessionInformation(): Promise<void> {
  sessionInformation.value = await waitForSessionInformation();
}

function buildCreateRequestSeed(definitionId: number): FullProcedureRequestDTO {
  const definition = definitionsById.value[definitionId] || null;

  return {
    companyId: (sessionInformation.value as any)?.companyId ?? null,
    employeeId: currentEmployee.value?.id ?? null,
    firstname: currentEmployee.value?.firstname ?? null,
    lastname: currentEmployee.value?.lastname ?? null,
    requestStatus: ProcedureRequestStatus.PREPARING,
    requestType: definitionId,
    quantity: definition?.defaultQuantity ?? null,
    activityTypeId: definition?.activityTypeId ?? null,
    hoursBagDelta: 0,
  } as FullProcedureRequestDTO;
}

function cloneDraftState(
  draft: ProcedureRequestDialogDraftState | null | undefined
): ProcedureRequestDialogDraftState | null {
  if (!draft) {
    return null;
  }

  return {
    requestTitle: draft.requestTitle || "",
    requestInfo: draft.requestInfo || "",
    fromDate: draft.fromDate ? { ...draft.fromDate } : null,
    toDate: draft.toDate ? { ...draft.toDate } : null,
    fromTimeText: draft.fromTimeText || "",
    toTimeText: draft.toTimeText || "",
    vacationDaysText: draft.vacationDaysText || "",
    quantity: draft.quantity || "",
    projectId: draft.projectId ?? null,
    activityTypeId: draft.activityTypeId ?? null,
    hoursBagType: draft.hoursBagType ?? null,
    paidAbsenceCategoryId: draft.paidAbsenceCategoryId ?? null,
    approverComment: draft.approverComment || "",
    documents: cloneProcedureDocuments(draft.documents),
  };
}

function cloneProcedureDocuments(
  documents: ProcedureDocumentDTO[] | null | undefined
): ProcedureDocumentDTO[] {
  return (documents || []).map((document) => ({
    id: document.id,
    documentName: document.documentName ?? null,
  }));
}

function parseProcedureDocumentIds(documentIds?: string | null): number[] {
  if (!documentIds?.trim()) {
    return [];
  }

  return documentIds
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isFinite(item));
}

function syncDialogDocuments(request?: FullProcedureRequestDTO | null): void {
  const documents = cloneProcedureDocuments(request?.documents);
  const fallbackIds = parseProcedureDocumentIds(request?.documentIds);
  const resolvedIds = documents
    .map((document) => document.id)
    .filter((documentId): documentId is number => documentId != null);

  requestDialogDocuments.value = documents;
  requestDialogDocumentIds.value =
    resolvedIds.length > 0 ? resolvedIds : fallbackIds;
  syncDraftSnapshotDocuments();
}

function syncDraftSnapshotDocuments(): void {
  const baseDraft =
    requestDialogDraftSnapshot.value ||
    requestDialogDraftSeed.value ||
    cloneDraftState(createEmptyDraftState());

  const nextDraft = cloneDraftState(baseDraft) || createEmptyDraftState();
  nextDraft.documents = cloneProcedureDocuments(requestDialogDocuments.value);
  requestDialogDraftSnapshot.value = nextDraft;
}

function createEmptyDraftState(): ProcedureRequestDialogDraftState {
  return {
    requestTitle: "",
    requestInfo: "",
    fromDate: null,
    toDate: null,
    fromTimeText: "",
    toTimeText: "",
    vacationDaysText: "",
    quantity: "",
    projectId: null,
    activityTypeId: null,
    hoursBagType: null,
    paidAbsenceCategoryId: null,
    approverComment: "",
    documents: [],
  };
}

function readPersistedDialogState(): PersistedProcedureDialogState | null {
  try {
    const raw = window.sessionStorage.getItem(PROCEDURE_DIALOG_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<PersistedProcedureDialogState> | null;
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    const mode = parsed.mode;
    if (
      mode !== "create" &&
      mode !== "edit" &&
      mode !== "approve" &&
      mode !== "read-only"
    ) {
      return null;
    }

    return {
      version: 1,
      mode,
      requestId: parseNumericIdentifier(parsed.requestId),
      definitionId: parseNumericIdentifier(parsed.definitionId),
      draft: cloneDraftState(parsed.draft || null),
    };
  } catch {
    return null;
  }
}

function clearPersistedDialogState(): void {
  try {
    window.sessionStorage.removeItem(PROCEDURE_DIALOG_STORAGE_KEY);
  } catch {
    // Ignore storage failures; the dialog can still work without restore.
  }
}

function persistDialogStateForCalendar(): void {
  if (!showRequestDialog.value || requestDialogMode.value === "read-only") {
    clearPersistedDialogState();
    return;
  }

  const draft = cloneDraftState(
    requestDialogDraftSnapshot.value || requestDialogDraftSeed.value
  );

  const persistedState: PersistedProcedureDialogState = {
    version: 1,
    mode: requestDialogMode.value,
    requestId: requestDialogRequestId.value,
    definitionId:
      requestDialogDefinitionId.value ??
      selectedDialogDefinition.value?.id ??
      selectedDialogRequest.value?.requestType ??
      null,
    draft,
  };

  try {
    window.sessionStorage.setItem(
      PROCEDURE_DIALOG_STORAGE_KEY,
      JSON.stringify(persistedState)
    );
  } catch {
    // Ignore storage failures; calendar navigation should still proceed.
  }
}

function resetRequestDialogState(): void {
  requestDialogRequestId.value = null;
  requestDialogDefinitionId.value = null;
  requestDialogMode.value = "read-only";
  requestDialogLoading.value = false;
  requestDialogActionLoading.value = false;
  requestDialogAttachmentLoading.value = false;
  requestDialogError.value = false;
  requestDialogNotFound.value = false;
  requestDialogDraftSeed.value = null;
  requestDialogDraftSnapshot.value = null;
  pendingProcedureDialogConfirmAction.value = null;
  requestDialogDocuments.value = [];
  requestDialogDocumentIds.value = [];
  selectedRequestSummary.value = null;
  selectedRequestDetail.value = null;
  dialogProjectOptions.value = [];
  dialogProjectOptionsLoading.value = false;
  dialogAbsenceCategoryOptions.value = [];
  dialogAbsenceCategoryOptionsLoading.value = false;
}

function shouldLoadAbsenceCategories(
  definition: FullProcedureDefinitionDTO | null,
  request: FullProcedureRequestDTO | null
): boolean {
  if (!definition?.enableAbsenceCategory) {
    return false;
  }

  return definition.procedureClass === "VACATION_LEAVE" && !definition.showVacationDays && request?.employeeId != null;
}

function loadDialogProjectOptions(
  request: FullProcedureRequestDTO | null,
  definition: FullProcedureDefinitionDTO | null
): void {
  dialogProjectOptions.value = [];
  dialogProjectOptionsLoading.value = false;

  if (!definition?.showProject || !request?.employeeId) {
    return;
  }

  const currentEmployeeId = currentEmployee.value?.id ?? null;
  if (currentEmployeeId != null && request.employeeId === currentEmployeeId) {
    const options = (((sessionInformation.value as any)?.employeeProjects || []) as Project[])
      .filter((project) => project?.id != null && project.enabled !== false)
      .map((project) => ({
        value: project.id,
        label: formatProjectOptionLabel(project),
      }));

    if (request.projectId != null && !options.some((project) => project.value === request.projectId)) {
      options.push({
        value: request.projectId,
        label: formatProjectOptionLabel({
          id: request.projectId,
          shortName: request.projectShortName ?? undefined,
          description: request.projectDescription ?? undefined,
        } as Project),
      });
    }

    dialogProjectOptions.value = options.sort((left, right) => left.label.localeCompare(right.label));
    return;
  }

  dialogProjectOptionsLoading.value = true;

  proceduresApi.getProjectsByEmployee(
    request.employeeId,
    request.id ?? -1,
    (projects) => {
      const options = (projects || [])
        .filter((project) => project?.id != null && project.enabled !== false)
        .map((project) => ({
          value: project.id,
          label: formatProjectOptionLabel(project),
        }));

      if (request.projectId != null && !options.some((project) => project.value === request.projectId)) {
        options.push({
          value: request.projectId,
          label: formatProjectOptionLabel({
            id: request.projectId,
            shortName: request.projectShortName ?? undefined,
            description: request.projectDescription ?? undefined,
          } as Project),
        });
      }

      dialogProjectOptions.value = options.sort((left, right) => left.label.localeCompare(right.label));
      dialogProjectOptionsLoading.value = false;
    },
    () => {
      dialogProjectOptionsLoading.value = false;
    }
  );
}

function loadDialogAbsenceCategoryOptions(
  request: FullProcedureRequestDTO | null,
  definition: FullProcedureDefinitionDTO | null
): void {
  dialogAbsenceCategoryOptions.value = [];
  dialogAbsenceCategoryOptionsLoading.value = false;

  if (!shouldLoadAbsenceCategories(definition, request)) {
    return;
  }

  const allowedIds = new Set(
    String(definition?.paidAbsencesCategories || "")
      .split(",")
      .map((token) => Number(token.trim()))
      .filter((value) => Number.isFinite(value))
  );
  const selectedCategoryId = request?.paidAbsenceCategoryId ?? null;

  dialogAbsenceCategoryOptionsLoading.value = true;

  proceduresApi.getPaidAbsenceCategories(
    (categories) => {
      const options = (categories || [])
        .filter((category) => {
          if (!category?.id || category.isEnabled === false) {
            return false;
          }

          return allowedIds.size === 0 || allowedIds.has(category.id);
        })
        .map((category) => ({
          value: category.id,
          label: category.name?.trim() || String(category.id),
        }));

      const finalizeOptions = (extraOption?: { label: string; value: number | null }) => {
        dialogAbsenceCategoryOptions.value = [
          {
            value: null,
            label: t("wkt.client.procedures.edit-window.no_absence_category"),
          },
          ...options,
          ...(extraOption ? [extraOption] : []),
        ].filter(
          (option, index, list) =>
            list.findIndex((candidate) => candidate.value === option.value) === index
        );
        dialogAbsenceCategoryOptionsLoading.value = false;
      };

      if (
        selectedCategoryId != null &&
        !options.some((category) => category.value === selectedCategoryId)
      ) {
        proceduresApi.getPaidAbsenceCategoryById(
          selectedCategoryId,
          (category: PaidAbsenceCategoryDTO) => {
            finalizeOptions({
              value: category.id,
              label: category.name?.trim() || String(category.id),
            });
          },
          () => finalizeOptions()
        );
        return;
      }

      finalizeOptions();
    },
    () => {
      dialogAbsenceCategoryOptionsLoading.value = false;
    }
  );
}

function loadDialogSupportingData(
  request: FullProcedureRequestDTO | null,
  definition: FullProcedureDefinitionDTO | null
): void {
  loadDialogProjectOptions(request, definition);
  loadDialogAbsenceCategoryOptions(request, definition);
}

function hasLegacyVacationBreakdown(
  request?: FullProcedureRequestDTO | null
): boolean {
  return Boolean(
    request &&
      request.vacationDeltaOld != null &&
      request.vacationDeltaOld !== 0 &&
      request.extraDeltaOld != null &&
      request.extraDeltaOld !== 0
  );
}

function shouldOpenLegacyVacationReadOnly(
  definition: FullProcedureDefinitionDTO | null,
  request?: FullProcedureRequestDTO | null
): boolean {
  return (
    definition?.procedureClass === "VACATION_LEAVE" &&
    hasLegacyVacationBreakdown(request)
  );
}

function resolveRequestDialogContext(
  requestId: number
): {
  definition: FullProcedureDefinitionDTO;
  request: FullProcedureRequestDTO;
} | null {
  const request = findRequestById(requestId);
  const definition = request ? definitionForRequest(request) : null;

  if (!request || !definition) {
    showAlert({
      text: t("wkt.client.common.error.try_again_later"),
      color: "error",
      timeout: 5000,
    });
    return null;
  }

  return {
    definition,
    request,
  };
}

function resolveRequestTitle(
  payload: ProcedureRequestDialogSubmitPayload,
  definition: FullProcedureDefinitionDTO,
  request: FullProcedureRequestDTO | null
): string {
  const trimmedTitle = payload.requestTitle.trim();
  if (trimmedTitle) {
    return trimmedTitle;
  }

  if (payload.projectId != null) {
    const project = dialogProjectOptions.value.find((option) => option.value === payload.projectId);
    if (project?.label) {
      return project.label;
    }
  }

  return request?.requestTitle?.trim() || definition.name?.trim() || t("wkt.client.procedures.title");
}

function buildProcedureSubmitRequest(
  payload: ProcedureRequestDialogSubmitPayload,
  mode: ProcedureRequestDialogMode,
  statusOverride?: number
): Record<string, unknown> | null {
  const definition = selectedDialogDefinition.value;
  const request = selectedDialogRequest.value;
  const employeeId = request?.employeeId ?? currentEmployee.value?.id ?? null;
  const companyId = request?.companyId ?? (sessionInformation.value as any)?.companyId ?? null;
  const requestType = request?.requestType ?? requestDialogDefinitionId.value ?? null;
  const documentIds =
    requestDialogDocumentIds.value.length > 0
      ? requestDialogDocumentIds.value.join(",")
      : null;

  if (!definition || employeeId == null || companyId == null || requestType == null) {
    return null;
  }

  const nextRequestStatus =
    statusOverride ??
    (mode === "approve"
      ? ProcedureRequestStatus.APPROVED
      : request?.requestStatus ?? ProcedureRequestStatus.PREPARING);

  const isVacationApprove =
    mode === "approve" && definition.procedureClass === "VACATION_LEAVE";

  const quantity =
    isVacationApprove
      ? request?.quantity ?? payload.quantity
      : definition.procedureClass === "VACATION_LEAVE" && definition.requestSubtype === 8
        ? payload.quantity
        : definition.showQuantity
          ? payload.quantity
          : definition.defaultQuantity ?? payload.quantity;

  const normalizedToDate =
    definition.showFromDate &&
    !definition.showToDate &&
    definition.showFromTime &&
    definition.showToTime
      ? payload.fromDate
      : definition.showToDate
        ? payload.toDate
        : null;

  return {
    id: request?.id ?? null,
    companyId,
    employeeId,
    adminId: request?.adminId ?? null,
    requestType,
    requestStatus: nextRequestStatus,
    requestTitle: resolveRequestTitle(payload, definition, request),
    requestInfo: definition.showFreeText ? payload.requestInfo || null : null,
    fromDate: definition.showFromDate ? payload.fromDate : null,
    toDate: normalizedToDate,
    fromHour: definition.showFromTime ? payload.fromHour : null,
    toHour: definition.showToTime ? payload.toHour : null,
    quantity,
    projectId: definition.showProject ? payload.projectId : null,
    activityTypeId:
      payload.activityTypeId ??
      definition.activityTypeId ??
      request?.activityTypeId ??
      null,
    hoursBagType:
      definition.showVacationDays
        ? isVacationApprove
          ? request?.hoursBagType ?? payload.hoursBagType
          : payload.hoursBagType
        : null,
    hoursBagDelta:
      definition.procedureClass === "VACATION_LEAVE"
        ? isVacationApprove
          ? request?.hoursBagDelta ?? payload.hoursBagDelta
          : payload.hoursBagDelta
        : request?.hoursBagDelta ?? null,
    extraDeltaOld: request?.extraDeltaOld ?? 0,
    vacationDeltaOld: request?.vacationDeltaOld ?? 0,
    manualCalculationVacation: request?.manualCalculationVacation ?? false,
    approverComment:
      mode === "approve" || nextRequestStatus === ProcedureRequestStatus.DECLINED
        ? payload.approverComment || null
        : request?.approverComment ?? null,
    requestReference: request?.requestReference ?? null,
    expenseSheetId: request?.expenseSheetId ?? null,
    paidAbsenceCategory:
      definition.enableAbsenceCategory && !definition.showVacationDays
        ? payload.paidAbsenceCategoryId
        : null,
    documentIds,
  };
}

function handleProcedureActionSuccess(response?: GenericServiceResponseDTO | ProcedureServiceResponseDTO | null): void {
  if (!response?.rsOK) {
    showAlert({
      text: response?.message || t("wkt.client.common.error.try_again_later"),
      color: "error",
      timeout: 5000,
    });
    return;
  }

  if (response?.message) {
    showAlert({
      text: response.message,
      color: "success",
    });
  }
  showRequestDialog.value = false;
  resetRequestDialogState();
  loadProcedures();
}

function openDialogForCreate(
  definitionId: number,
  draftSeed?: ProcedureRequestDialogDraftState | null
): void {
  resetRequestDialogState();
  requestDialogMode.value = "create";
  requestDialogDefinitionId.value = definitionId;
  requestDialogDraftSeed.value = cloneDraftState(draftSeed);
  requestDialogDraftSnapshot.value = cloneDraftState(draftSeed);
  selectedRequestSummary.value = buildCreateRequestSeed(definitionId);
  syncDialogDocuments({
    documents: draftSeed?.documents || [],
    documentIds: null,
  } as FullProcedureRequestDTO);
  loadDialogSupportingData(selectedRequestSummary.value, definitionsById.value[definitionId] || null);
  showRequestDialog.value = true;
}

function openDialogForExistingRequest(
  requestId: number,
  mode: ProcedureRequestDialogMode,
  draftSeed?: ProcedureRequestDialogDraftState | null
): void {
  resetRequestDialogState();
  requestDialogMode.value = mode;
  requestDialogRequestId.value = requestId;
  requestDialogDraftSeed.value = cloneDraftState(draftSeed);
  requestDialogDraftSnapshot.value = cloneDraftState(draftSeed);
  selectedRequestSummary.value = findRequestById(requestId);
  syncDialogDocuments(selectedRequestSummary.value);
  requestDialogDefinitionId.value = selectedRequestSummary.value?.requestType ?? null;
  requestDialogLoading.value = true;
  showRequestDialog.value = true;

  proceduresApi.getProcedureRequest(
    requestId,
    (response) => {
      if (requestDialogRequestId.value !== requestId) {
        return;
      }

      selectedRequestDetail.value = response;
      syncDialogDocuments({
        ...response,
        documents:
          draftSeed?.documents !== undefined
            ? draftSeed.documents
            : response?.documents,
      } as FullProcedureRequestDTO);
      requestDialogDefinitionId.value = response?.requestType ?? requestDialogDefinitionId.value;
      requestDialogLoading.value = false;
      requestDialogError.value = false;
      requestDialogNotFound.value = false;
      loadDialogSupportingData(response, definitionsById.value[response?.requestType || -1] || null);
    },
    (requestError: AxiosError) => {
      if (requestDialogRequestId.value !== requestId) {
        return;
      }

      selectedRequestDetail.value = null;
      requestDialogLoading.value = false;
      requestDialogNotFound.value = requestError.response?.status === 404;
      requestDialogError.value = !requestDialogNotFound.value;
    }
  );
}

function applyDialogPayload(dialog?: ProcedureDialogPayload | null): void {
  if (!dialog) {
    return;
  }

  const requestId = parseNumericIdentifier(dialog.requestId);
  const definitionId = parseNumericIdentifier(dialog.definitionId);
  const mode = dialog.mode ?? "read-only";

  if (mode === "create" && definitionId != null) {
    openDialogForCreate(definitionId);
    return;
  }

  if (requestId != null) {
    openDialogForExistingRequest(requestId, mode);
  }
}

function restoreDialogStateFromSession(): boolean {
  const persistedState = readPersistedDialogState();
  clearPersistedDialogState();

  if (!persistedState) {
    return false;
  }

  if (persistedState.mode === "create" && persistedState.requestId != null) {
    openDialogForExistingRequest(
      persistedState.requestId,
      persistedState.mode,
      persistedState.draft
    );
    return true;
  }

  if (persistedState.mode === "create" && persistedState.definitionId != null) {
    openDialogForCreate(persistedState.definitionId, persistedState.draft);
    return true;
  }

  if (persistedState.requestId != null) {
    openDialogForExistingRequest(
      persistedState.requestId,
      persistedState.mode,
      persistedState.draft
    );
    return true;
  }

  return false;
}

function handleResize(): void {
  isDesktop.value = window.innerWidth >= 992;
}

function loadProcedures(): void {
  loading.value = true;
  error.value = false;

  proceduresApi.getProcedures(
    [
      ProcedureRequestStatus.PENDING,
      ProcedureRequestStatus.APPROVED,
      ProcedureRequestStatus.DECLINED,
      ProcedureRequestStatus.CANCELED,
      ProcedureRequestStatus.PROCESSED,
    ],
    (response) => {
      procedureData.value = {
        procedureRequests: response?.procedureRequests || [],
        approverRequests: response?.approverRequests || [],
        approverEmployees: response?.approverEmployees || [],
      };
      loading.value = false;
    },
    () => {
      error.value = true;
      loading.value = false;
    }
  );
}

function openCreateRequest(): void {
  if (!enabledDefinitions.value.length) {
    return;
  }

  if (enabledDefinitions.value.length === 1) {
    const [definition] = enabledDefinitions.value;
    openDialogForCreate(definition.id);
    return;
  }

  showSelectorDialog.value = true;
}

function selectDefinition(definitionId: number): void {
  showSelectorDialog.value = false;
  openDialogForCreate(definitionId);
}

function editRequest(requestId: number): void {
  const context = resolveRequestDialogContext(requestId);
  if (!context) {
    return;
  }

  const { definition, request } = context;

  if (shouldOpenLegacyVacationReadOnly(definition, request)) {
    openDialogForExistingRequest(requestId, "read-only");
    return;
  }

  openDialogForExistingRequest(requestId, "edit");
}

function showHistoricalRequest(requestId: number): void {
  openDialogForExistingRequest(requestId, "read-only");
}

function retryShowHistoricalRequest(): void {
  if (requestDialogRequestId.value == null) {
    return;
  }

  openDialogForExistingRequest(requestDialogRequestId.value, requestDialogMode.value);
}

function handleRequestDialogVisibility(visible: boolean): void {
  showRequestDialog.value = visible;

  if (visible) {
    return;
  }

  const draftRequestId = requestDialogRequestId.value;
  const shouldDeleteDraftRequest =
    requestDialogMode.value === "create" &&
    draftRequestId != null &&
    !requestDialogActionLoading.value &&
    !requestDialogAttachmentLoading.value;

  if (shouldDeleteDraftRequest) {
    proceduresApi.cancelProcedureRequest(
      draftRequestId,
      () => {
        resetRequestDialogState();
      },
      () => {
        resetRequestDialogState();
      }
    );
    return;
  }

  resetRequestDialogState();
}

function handleRequestDialogDraftChange(
  draft: ProcedureRequestDialogDraftState
): void {
  const nextDraft = cloneDraftState(draft) || createEmptyDraftState();
  nextDraft.documents = cloneProcedureDocuments(requestDialogDocuments.value);
  requestDialogDraftSnapshot.value = nextDraft;
}

const requestActionConfirmDialogTitle = computed(() => {
  if (pendingProcedureDialogConfirmAction.value?.type === "decline") {
    return t("wkt.client.checkin.confirm_pocedure_decline_window.title");
  }

  return t("wkt.client.checkin.confirm_pocedure_delete_window.title");
});

const requestActionConfirmDialogMessage = computed(() => {
  if (pendingProcedureDialogConfirmAction.value?.type === "decline") {
    return t("wkt.client.checkin.confirm_procedure_decline_window.title_description");
  }

  return t("wkt.client.checkin.confirm_procedure_delete_window.title_description");
});

const requestActionConfirmDialogOkLabel = computed(() => {
  if (pendingProcedureDialogConfirmAction.value?.type === "decline") {
    return t("wkt.client.common.dialog-buttons.decline");
  }

  return t("wkt.client.common.dialog-buttons.delete");
});

function openProcedureDialogConfirmAction(
  action: ProcedureDialogConfirmAction
): void {
  pendingProcedureDialogConfirmAction.value = action;
  requestActionConfirmDialog.value?.confirm(action);
}

function confirmProcedureDialogAction(): void {
  const action = pendingProcedureDialogConfirmAction.value;
  pendingProcedureDialogConfirmAction.value = null;

  if (!action) {
    return;
  }

  if (action.type === "decline") {
    executeDeclineProcedureDialog(action.payload);
    return;
  }

  executeDeleteProcedureDialog();
}

function materializeDraftRequest(upload: ProcedureDocumentUploadResponseDTO): void {
  requestDialogRequestId.value = upload.requestId;

  if (selectedRequestSummary.value) {
    selectedRequestSummary.value = {
      ...selectedRequestSummary.value,
      id: upload.requestId,
    };
  }

  if (selectedRequestDetail.value) {
    selectedRequestDetail.value = {
      ...selectedRequestDetail.value,
      id: upload.requestId,
    };
  }
}

function uploadProcedureDocument(file: File): void {
  const definition = selectedDialogDefinition.value;
  const request = selectedDialogRequest.value;
  const requestType = request?.requestType ?? requestDialogDefinitionId.value;

  if (!definition || requestType == null) {
    showAlert({
      text: t("wkt.client.common.error.try_again_later"),
      color: "error",
    });
    return;
  }

  requestDialogAttachmentLoading.value = true;

  proceduresApi.uploadProcedureDocument(
    file,
    {
      requestId: request?.id ?? -1,
      requestTitle: request?.requestTitle ?? requestDialogDraftSnapshot.value?.requestTitle ?? definition.name,
      requestType,
      documentCategoryId: definition.documentCategoryId ?? null,
    },
    (uploadResponse) => {
      materializeDraftRequest(uploadResponse);

      documentsApi.getDocumentById(
        uploadResponse.documentId,
        (documentResponse) => {
          const uploadedDocument = documentResponse as ProcedureDocumentDTO;
          requestDialogDocuments.value = [
            ...requestDialogDocuments.value,
            {
              id: uploadedDocument?.id ?? uploadResponse.documentId,
              documentName: uploadedDocument?.documentName ?? file.name,
            },
          ];
          requestDialogDocumentIds.value = requestDialogDocuments.value
            .map((document) => document.id)
            .filter((documentId): documentId is number => documentId != null);
          syncDraftSnapshotDocuments();
          requestDialogAttachmentLoading.value = false;
        },
        () => {
          requestDialogDocuments.value = [
            ...requestDialogDocuments.value,
            {
              id: uploadResponse.documentId,
              documentName: file.name,
            },
          ];
          requestDialogDocumentIds.value = requestDialogDocuments.value
            .map((document) => document.id)
            .filter((documentId): documentId is number => documentId != null);
          syncDraftSnapshotDocuments();
          requestDialogAttachmentLoading.value = false;
        }
      );
    },
    (requestError: AxiosError) => {
      requestDialogAttachmentLoading.value = false;
      const responseData = requestError?.response?.data as { message?: string | null } | undefined;
      showAlert({
        text: responseData?.message || requestError?.message || t("wkt.client.common.error.try_again_later"),
        color: "error",
        timeout: 6000,
      });
    }
  );
}

function removeProcedureDocument(payload: { documentId: number }): void {
  requestDialogDocuments.value = requestDialogDocuments.value.filter(
    (document) => document.id !== payload.documentId
  );
  requestDialogDocumentIds.value = requestDialogDocumentIds.value.filter(
    (documentId) => documentId !== payload.documentId
  );
  syncDraftSnapshotDocuments();
}

function submitProcedureDialog(payload: ProcedureRequestDialogSubmitPayload): void {
  const apiPayload = buildProcedureSubmitRequest(payload, requestDialogMode.value);
  if (!apiPayload) {
    showAlert({
      text: t("wkt.client.common.error.try_again_later"),
      color: "error",
    });
    return;
  }

  requestDialogActionLoading.value = true;

  proceduresApi.storeProcedure(
    apiPayload,
    (response) => {
      requestDialogActionLoading.value = false;
      handleProcedureActionSuccess(response);
    },
    () => {
      requestDialogActionLoading.value = false;
      showAlert({
        text: t("wkt.client.common.error.try_again_later"),
        color: "error",
        timeout: 5000,
      });
    }
  );
}

function declineProcedureDialog(payload: ProcedureRequestDialogSubmitPayload): void {
  openProcedureDialogConfirmAction({
    type: "decline",
    payload,
  });
}

function executeDeclineProcedureDialog(
  payload: ProcedureRequestDialogSubmitPayload
): void {
  const apiPayload = buildProcedureSubmitRequest(
    payload,
    requestDialogMode.value,
    ProcedureRequestStatus.DECLINED
  );
  if (!apiPayload) {
    showAlert({
      text: t("wkt.client.common.error.try_again_later"),
      color: "error",
    });
    return;
  }

  requestDialogActionLoading.value = true;

  proceduresApi.storeProcedure(
    apiPayload,
    (response) => {
      requestDialogActionLoading.value = false;
      handleProcedureActionSuccess(response);
    },
    () => {
      requestDialogActionLoading.value = false;
      showAlert({
        text: t("wkt.client.common.error.try_again_later"),
        color: "error",
        timeout: 5000,
      });
    }
  );
}

function deleteProcedureDialog(): void {
  openProcedureDialogConfirmAction({ type: "delete" });
}

function executeDeleteProcedureDialog(): void {
  if (selectedDialogRequest.value?.id == null) {
    return;
  }

  requestDialogActionLoading.value = true;

  proceduresApi.cancelProcedureRequest(
    selectedDialogRequest.value.id,
    (response) => {
      requestDialogActionLoading.value = false;
      handleProcedureActionSuccess(response);
    },
    () => {
      requestDialogActionLoading.value = false;
      showAlert({
        text: t("wkt.client.common.error.try_again_later"),
        color: "error",
        timeout: 5000,
      });
    }
  );
}

function approveRequest(requestId: number): void {
  const context = resolveRequestDialogContext(requestId);
  if (!context) {
    return;
  }

  const { definition, request } = context;

  if (shouldOpenLegacyVacationReadOnly(definition, request)) {
    openDialogForExistingRequest(requestId, "read-only");
    return;
  }

  openDialogForExistingRequest(requestId, "approve");
}

function denyRequest(requestId: number): void {
  const context = resolveRequestDialogContext(requestId);
  if (!context) {
    return;
  }

  const { definition, request } = context;

  if (shouldOpenLegacyVacationReadOnly(definition, request)) {
    openDialogForExistingRequest(requestId, "read-only");
    return;
  }

  openDialogForExistingRequest(requestId, "approve");
}

function showEmployeeDetails(employeeId?: number | null): void {
  if (!employeeId) {
    return;
  }
  gwtApi.profileShowEmployeeDetails(employeeId);
}

function openOwnCalendar(): void {
  persistDialogStateForCalendar();
  gwtApi.proceduresOpenOwnCalendar();
}

function openApproverCalendar(requestId: number): void {
  persistDialogStateForCalendar();
  gwtApi.proceduresOpenApproverCalendar(requestId);
}

function showExpenseSheet(expenseSheetId: number): void {
  gwtApi.proceduresShowExpenseSheet(expenseSheetId);
}

async function onReveal(payload?: unknown): Promise<void> {
  const parsedPayload = parseProceduresModulePayload(payload);

  showModule.value = true;
  loading.value = parsedPayload?.loading !== false;
  await ensureSessionInformation();
  loadProcedures();
  if (parsedPayload?.dialog) {
    clearPersistedDialogState();
    applyDialogPayload(parsedPayload.dialog);
    return;
  }

  restoreDialogStateFromSession();
}

async function refreshModule(): Promise<void> {
  if (!showModule.value) {
    showModule.value = true;
  }
  await ensureSessionInformation();
  loadProcedures();
}

defineExpose({
  onReveal,
  refreshModule,
});

onMounted(() => {
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});
</script>

<style scoped>
.procedures-module {
  display: flex;
  flex-direction: column;
  background: #fff;
  min-height: 100%;
  width: 100%;
}

.procedures-shell {
  padding: 0 0 2rem;
}

.procedures-state,
.procedures-error,
.procedures-empty {
  min-height: 16rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.procedures-empty--section {
  min-height: 11rem;
  border: 1px dashed #d9e2ec;
  border-radius: 12px;
  background: #fff;
}

.procedures-skeleton {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.procedures-skeleton--mobile {
  padding-inline: 0.5rem;
}

.procedures-skeleton__panel {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.procedures-skeleton-card {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  padding: 0.95rem;
  border: 1px solid #d9e2ec;
  border-radius: 12px;
  background: #fff;
}

.procedures-skeleton-card__header,
.procedures-skeleton-card__employee,
.procedures-skeleton-card__actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.procedures-skeleton-card__header {
  align-items: flex-start;
}

.procedures-skeleton-card__header-copy,
.procedures-skeleton-card__employee-copy {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
  flex: 1;
}

.procedures-skeleton-card__details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.procedures-skeleton-card__actions {
  justify-content: flex-end;
}

.procedures-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.procedures-section {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.procedures-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.procedures-section__title {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
}

.procedures-section__title i {
  color: var(--primary-color);
  font-size: 1rem;
}

.procedures-section__controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-left: auto;
}

.procedures-section__header h2 {
  margin: 0;
  font-size: 1.1rem;
  color: #263238;
}

.procedures-section__count {
  min-width: 1.6rem;
  height: 1.6rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--primary-rgb), 0.1);
  color: var(--primary-color);
  font-size: 0.8rem;
  font-weight: 600;
}

.procedures-card-grid {
  display: grid;
  gap: 0.75rem;
}

.procedures-filter {
  min-width: 15rem;
}

@media (min-width: 768px) {
  .procedures-shell {
    padding: 0.5rem 0.75rem 2rem;
  }
}

@media (max-width: 767px) {
  .procedures-shell {
    padding: 0 0 calc(5.5rem + env(safe-area-inset-bottom));
  }

  .procedures-content,
  .procedures-section {
    padding-inline: 0.5rem;
  }

  .procedures-content {
    padding-bottom: calc(2rem + env(safe-area-inset-bottom));
  }

  .procedures-section__header {
    padding-inline: 0.1rem;
    flex-wrap: wrap;
  }

  .procedures-section__header h2 {
    font-size: 1rem;
  }

  .procedures-section__controls {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 0.5rem;
    margin-left: 0;
  }

  .procedures-filter {
    width: 100%;
    min-width: 0;
  }

  .procedures-card-grid {
    gap: 0.65rem;
  }

  :deep(.procedures-tabs.p-tabs .p-tablist) {
    margin-top: 0;
  }

  :deep(.procedures-tabs.p-tabs .p-tabpanels) {
    padding: 20px 0 calc(5.5rem + env(safe-area-inset-bottom)) !important;
  }

  :deep(.procedures-tabs.p-tabs .p-tabpanel) {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
    padding-bottom: 0 !important;
  }

  .procedures-skeleton-card__details {
    grid-template-columns: 1fr;
  }
}
</style>
